import {
  Box,
  Button,
  Chip,
  DialogContent,
  FormControl,
  FormHelperText,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'components/Select';
import Input from 'components/Input';
import { useTranslation } from 'react-i18next';
import Icon from 'components/Icon';
import CopyButton from 'components/CopyButton/CopyButton';
import { DialogActions } from 'components/Dialog';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectDefaultRole, selectRoles } from 'features/permissions/permissionsSlice';
import { closeModal } from 'features/modal/modalSlice';
import CodeBox from 'components/CodeBox';
import { sendInvitationEmailsRequested } from 'features/users/actions';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { v4 as uuidv4 } from 'uuid';
import {
  createInviteParticipantSchema,
  InviteParticipantFormValues,
} from 'features/users/modals/invite-user/schema';
import { InvitedParticipant } from 'features/users/modals/invite-user/types';
import { selectIsEmbedded, selectSDKPublicRoomUrl } from 'features/sdk/sdkStateSlice';

const InviteUserAdvanced = () => {
  const { t } = useTranslation(['participants', 'modals']);
  const dispatch = useAppDispatch();

  const roles = useAppSelector(selectRoles);
  const defaultRole = useAppSelector(selectDefaultRole);

  const isEmbed = useAppSelector(selectIsEmbedded);
  const sdkPublicRoomUrl = useAppSelector(selectSDKPublicRoomUrl);

  const [participants, setParticipants] = useState<InvitedParticipant[]>([]);

  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const prevParticipantCountRef = useRef(0);

  const schema = useMemo(() => createInviteParticipantSchema(participants), [participants]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<InviteParticipantFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: defaultRole,
    },
  });

  useEffect(() => {
    const wasParticipantAdded = participants.length > prevParticipantCountRef.current;
    prevParticipantCountRef.current = participants.length;

    if (wasParticipantAdded && chipsContainerRef.current) {
      chipsContainerRef.current.scrollTop = chipsContainerRef.current.scrollHeight;
    }
  }, [participants]);

  const handleAddParticipant: SubmitHandler<InviteParticipantFormValues> = (data) => {
    setParticipants((prev) => [
      ...prev,
      {
        id: uuidv4(),
        ...data,
      },
    ]);

    reset({ firstName: '', lastName: '', email: '', role: data.role });
  };

  const handleRemoveParticipant = (id: string) => {
    setParticipants((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSendEmails = () => {
    const users = participants.map((p) => ({
      firstName: p.firstName,
      lastName: p.lastName,
      role: p.role,
      email: p.email,
    }));

    dispatch(sendInvitationEmailsRequested(users));
    dispatch(closeModal('inviteUser'));
  };

  return (
    <>
      <DialogContent>
        <Typography gutterBottom>{t('invite_participants.instruction_text')}</Typography>

        <Stack spacing={3} sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl fullWidth error={!!errors.firstName}>
              <Controller
                name="firstName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <Input
                    {...field}
                    inputRef={ref}
                    placeholder={t('invite_participants.first_name_placeholder')}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth error={!!errors.lastName}>
              <Controller
                name="lastName"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <Input
                    {...field}
                    inputRef={ref}
                    placeholder={t('invite_participants.last_name_placeholder')}
                  />
                )}
              />
            </FormControl>
          </Box>

          <FormControl fullWidth>
            <Controller
              name="role"
              control={control}
              render={({ field: { ref, ...field } }) => (
                <Select {...field} inputRef={ref}>
                  {roles.map((role) => (
                    <MenuItem key={role.name} value={role.name}>
                      {role.displayName}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <FormControl fullWidth error={!!errors.email}>
              <Controller
                name="email"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <Input
                    {...field}
                    inputRef={ref}
                    placeholder={t('invite_participants.email_placeholder')}
                  />
                )}
              />
              {errors.email?.message && (
                <FormHelperText error>{errors.email.message}</FormHelperText>
              )}
            </FormControl>
            <Button
              variant="contained"
              onClick={handleSubmit(handleAddParticipant)}
              disabled={!isValid}
              sx={{
                fontWeight: 400,
                flexShrink: 0,
              }}
            >
              {t('invite_participants.btn_add_label')}
            </Button>
          </Box>
        </Stack>

        <Box
          ref={chipsContainerRef}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: '4px',
            p: 2,
            minHeight: 90,
            maxHeight: 90,
            overflowY: 'auto',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 6,
          }}
        >
          {participants.map((p) => (
            <Chip
              key={p.id}
              label={`${p.firstName} ${p.lastName}`.trim()}
              onDelete={() => handleRemoveParticipant(p.id)}
              deleteIcon={<Icon name="close" data-testid={`remove-participant-${p.email}`} />}
            />
          ))}
        </Box>

        {isEmbed && sdkPublicRoomUrl && (
          <Box>
            <Typography gutterBottom>{t('invite_participants.invite_link_label')}</Typography>
            <CodeBox
              sx={{
                overflowX: 'auto',
                width: '100%',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography component="span">{sdkPublicRoomUrl}</Typography>
            </CodeBox>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<Icon name="envelope-mail" />}
          onClick={handleSendEmails}
          disabled={participants.length === 0}
        >
          {t('modals:invite_participants.btn_send_email_label')}
        </Button>
        {isEmbed && sdkPublicRoomUrl && (
          <CopyButton text={sdkPublicRoomUrl} startIcon={<Icon name="copy" />}>
            {(isCopied) =>
              isCopied
                ? t('modals:invite_participants.btn_copied_label')
                : t('modals:invite_participants.btn_copy_label')
            }
          </CopyButton>
        )}
      </DialogActions>
    </>
  );
};

export default InviteUserAdvanced;
